import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/App/App.hooks.ts";
import { Panel } from "src/components/Panel/Panel.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { css } from "@emotion/react";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { TableView } from "src/components/Form/panels/CommentsPanel/components/TableView/TableView.tsx";
import { CreateCommentModal } from "src/components/Form/panels/CommentsPanel/components/CreateCommentModal/CreateCommentModal.tsx";
import dayjs from "dayjs";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useBoolean } from "src/hooks/use-boolean.ts";
import { Flex } from "src/components/Flex.tsx";
import { useResourceFormApi } from "src/components/Form/hooks/useResourceFormApi.ts";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { useResourceFormContext } from "src/components/Form/hooks/useResourceFormContext.ts";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";

await loadTranslations(TranslationNamespaceEnum.CommentsFormPanel);
function formatDateTime(value: string) {
  return dayjs(value).format("DD.MM.YYYY HH:mm");
}

export type CommentsPanelProps = {
  onSaveSuccess?: () => void;
  onSave: ({ content }: { content: string }) => Promise<void>;
  notes: {
    id: string;
    content: string;
    author: string;
    created_at: string;
  }[];
};
export function CommentsPanel(props: CommentsPanelProps) {
  const { notes = [], onSaveSuccess, onSave } = props;
  const {
    value: modalOpen,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean(false);
  const [content, setContent] = useState("");
  const formApi = useResourceFormApi();
  const formContext = useResourceFormContext();

  const { value: collapsed, toggle: toggleCollapsedState } = useBoolean(true);

  const { t } = useTranslation(TranslationNamespaceEnum.CommentsFormPanel);

  const rows = notes.map((row) => ({
    ...row,
    date: formatDateTime(row.created_at),
  }));

  const snackbar = useSnackbar();
  const notes_count = rows.length;

  const { mutate: onCreate, isPending: isSavingNote } = useMutation({
    mutationFn: async ({ content }: { content: string }) => {
      await onSave({ content: content });
    },
    onSuccess: () => {
      snackbar.success({
        flash: true,
        message: t("notification.noteWasCreated"),
      });
      formApi.refresh({
        resetForm: false,
        revalidateData: true,
      });
      onSaveSuccess?.();
      closeModal();
      setContent("");
    },
    onError: (e) => {
      snackbar.error({
        flash: true,
        message: t("notification.couldNotCreateNote"),
      });
    },
  });

  const handleSave = (value: string) => {
    onCreate({
      content: value,
    });
  };

  return (
    <Panel
      expandable
      expanded={!collapsed}
      onExpandedChange={toggleCollapsedState}
      defaultExpanded
    >
      <Panel.Header>
        <Flex alignItems={"center"}>
          <Panel.Title>{t("panel.title")}</Panel.Title>
          <TextV2
            css={css({
              marginLeft: 10,
            })}
            variant={"body2"}
          >
            ({notes_count})
          </TextV2>
        </Flex>
        <Panel.ExpandTrigger />
      </Panel.Header>
      <Panel.Content>
        <div>
          {!collapsed && (
            <TooltipV2
              disabled={formContext.action !== "create"}
              content={t("disabledFunctionality", {
                ns: TranslationNamespaceEnum.FormComponent,
              })}
            >
              <ButtonV2
                size="sm"
                startAdornment={<i className="icon-15-cross-circle" />}
                onClick={openModal}
                disabled={formContext.action === "create"}
              >
                {t("panel.action.text")}
              </ButtonV2>
            </TooltipV2>
          )}
        </div>

        {!collapsed && rows.length > 0 && (
          <div
            style={{
              paddingTop: 15,
            }}
          >
            <TableView rows={rows} />
          </div>
        )}
        {modalOpen && (
          <CreateCommentModal
            onClose={closeModal}
            content={content}
            isSaving={isSavingNote}
            onContentChange={setContent}
            onCancel={closeModal}
            onSave={handleSave}
          />
        )}
      </Panel.Content>
    </Panel>
  );
}
